import React from "react";
import { Box, Button, IconButton, Popover } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import OwnerChip from "./owner-chip";
import { ownerLabel } from "./owner-names";

export type OwnerSummary = {
  id: string;

  /** Null for an owner without a profile name. */
  name: string | null;
};

export type OwnersEditorProps = {

  /** Unique per editor instance; prefixes the element ids. */
  id: string;
  owners: OwnerSummary[];

  /** When false the owners render read-only with no add or remove affordances. */
  canEdit?: boolean;

  /** Owners with an in-flight change; their remove affordance is disabled. */
  pendingUserIds?: ReadonlySet<string>;
  onRemove?: (userId: string) => void;

  /**
   * Picker content for the "Add owner" popover. It is mounted only while the
   * popover is open, so a picker that queries its options fetches lazily.
   */
  renderPicker?: (close: () => void) => React.ReactNode;

  /** Shown read-only when there are no owners. */
  emptyLabel?: React.ReactNode;
};

const OwnersEditor: React.FC<OwnersEditorProps> = ({
  id,
  owners,
  canEdit = true,
  pendingUserIds,
  onRemove,
  renderPicker,
  emptyLabel = null,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const close = React.useCallback(() => setAnchorEl(null), []);
  const canAdd = canEdit && Boolean(renderPicker);
  const removable = canEdit && Boolean(onRemove);

  const removeRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const addRef = React.useRef<HTMLButtonElement | null>(null);
  // The remove button is disabled (and so blurred) while its change is pending,
  // so focus is placed once the change settles rather than on click.
  const focusAfterRemove = React.useRef<{ userId: string; index: number } | null>(null);

  React.useEffect(() => {
    const target = focusAfterRemove.current;
    if (!target || pendingUserIds?.has(target.userId)) return;
    focusAfterRemove.current = null;
    // A failed removal keeps the owner, so focus returns to its remove button.
    if (owners.some((owner) => owner.id === target.userId)) {
      removeRefs.current.get(target.userId)?.focus();
      return;
    }
    const next = owners[target.index] ?? (addRef.current ? null : owners[owners.length - 1]);
    (next ? removeRefs.current.get(next.id) : addRef.current)?.focus();
  }, [owners, pendingUserIds]);

  const requestRemove = (userId: string, index: number) => {
    focusAfterRemove.current = { userId, index };
    onRemove?.(userId);
  };

  return (
    <Box
      id={id}
      data-cy={canEdit ? "owners-editor" : "owners-editor-readonly"}
      sx={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
    >
      {owners.length === 0 && !canAdd && emptyLabel}

      {owners.map((owner, index) => {
        const pending = pendingUserIds?.has(owner.id) ?? false;
        const label = ownerLabel(owner.name);
        return (
          <OwnerChip
            key={owner.id}
            data-cy={`owner-chip-${owner.id}`}
            disabled={pending}
            label={
              // The remove button sits inside the label so it reads as part of the
              // chip and stays a named, focusable control (Chip's onDelete icon isn't).
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                {label}
                {removable && (
                  <IconButton
                    size="small"
                    aria-label={`Remove ${label} as owner`}
                    data-cy={`owner-remove-${owner.id}`}
                    disabled={pending}
                    ref={(node: HTMLButtonElement | null) => {
                      if (node) removeRefs.current.set(owner.id, node);
                      else removeRefs.current.delete(owner.id);
                    }}
                    onClick={() => requestRemove(owner.id, index)}
                    sx={{ ml: 0.25, p: 0.25 }}
                  >
                    <CancelIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            }
          />
        );
      })}

      {canAdd && (
        <Button
          id={`${id}-add`}
          ref={addRef}
          variant="text"
          size="small"
          aria-haspopup="dialog"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Add owner
        </Button>
      )}

      {canAdd && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { p: 1.5, width: 280 } } }}
        >
          <Box id={`${id}-picker`}>{renderPicker?.(close)}</Box>
        </Popover>
      )}
    </Box>
  );
};

export default OwnersEditor;
